import * as i0 from '@angular/core';
import { Component, Input, NgModule, EventEmitter, Output, HostBinding } from '@angular/core';
import { AlluvialChart, AreaChart, BoxplotChart, BubbleChart, BulletChart, CirclePackChart, ComboChart, DonutChart, ExperimentalChoroplethChart, GaugeChart, GroupedBarChart, HeatmapChart, HistogramChart, LineChart, LollipopChart, MeterChart, PieChart, RadarChart, ScatterChart, SimpleBarChart, StackedAreaChart, StackedBarChart, TreeChart, TreemapChart, WordCloudChart, arrowLeft, arrowRight, circle, diamond, square, tee, buildStraightPathString } from '@carbon/charts';
export { Alignments, ChartTheme, ScaleTypes } from '@carbon/charts';
import * as i1 from '@angular/common';
import { CommonModule } from '@angular/common';

/**
 * Wrapper around `BaseChart` in carbon charts library
 *
 * Most functions just call their equivalent from the chart library.
 */
class BaseChartComponent {
    /**
     * Data passed to charts library for displaying
     */
    set data(newData) {
        // If data already exists, that means the chart has been initialized
        const dataExistsAlready = !!this._data;
        this._data = newData;
        if (dataExistsAlready) {
            this.chart?.model.setData(newData);
        }
    }
    get data() {
        return this._data;
    }
    /**
     * Options passed to charts library
     */
    set options(newOptions) {
        // If data already exists, that means the chart has been initialized
        const optionsExistAlready = !!this._options;
        this._options = newOptions;
        if (optionsExistAlready) {
            this.chart?.model.setOptions(newOptions);
        }
    }
    get options() {
        return this._options;
    }
    constructor(elementRef) {
        this.elementRef = elementRef;
    }
    ngOnInit() {
        // Width prop is mandatory for the wrappers
        if (this.width) {
            this.options.width = this.width;
        }
        // Height prop is mandatory for the wrappers
        if (this.height) {
            this.options.height = this.height;
        }
    }
    /**
     * Runs after view init to create a chart, attach it to `elementRef` and draw it.
     */
    ngAfterViewInit() {
        console.log('Class that extended BaseChartComponent did not implement ngAfterViewInit().');
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: BaseChartComponent, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: BaseChartComponent, selector: "ibm-base-chart", inputs: { data: "data", options: "options", width: "width", height: "height" }, ngImport: i0, template: ``, isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: BaseChartComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'ibm-base-chart',
                    template: ``
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; }, propDecorators: { data: [{
                type: Input
            }], options: [{
                type: Input
            }], width: [{
                type: Input
            }], height: [{
                type: Input
            }] } });

/**
 * Wrapper around `Alluvial` in carbon charts library
 *
 * Most functions just call their equivalent from the chart library.
 */
class AlluvialChartComponent extends BaseChartComponent {
    /**
     * Runs after view init to create a chart, attach it to `elementRef` and draw it.
     */
    ngAfterViewInit() {
        this.chart = new AlluvialChart(this.elementRef.nativeElement, {
            data: this.data,
            options: this.options
        });
        Object.assign(this, this.chart);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AlluvialChartComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: AlluvialChartComponent, selector: "ibm-alluvial-chart", usesInheritance: true, ngImport: i0, template: ``, isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AlluvialChartComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'ibm-alluvial-chart',
                    template: ``
                }]
        }] });

/**
 * Wrapper around `AreaChart` in carbon charts library
 *
 * Most functions just call their equivalent from the chart library.
 */
class AreaChartComponent extends BaseChartComponent {
    /**
     * Runs after view init to create a chart, attach it to `elementRef` and draw it.
     */
    ngAfterViewInit() {
        this.chart = new AreaChart(this.elementRef.nativeElement, {
            data: this.data,
            options: this.options
        });
        Object.assign(this, this.chart);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AreaChartComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: AreaChartComponent, selector: "ibm-area-chart", usesInheritance: true, ngImport: i0, template: ``, isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AreaChartComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'ibm-area-chart',
                    template: ``
                }]
        }] });

/**
 * Wrapper around `BoxplotChart` in carbon charts library
 *
 * Most functions just call their equivalent from the chart library.
 */
class BoxplotChartComponent extends BaseChartComponent {
    /**
     * Runs after view init to create a chart, attach it to `elementRef` and draw it.
     */
    ngAfterViewInit() {
        this.chart = new BoxplotChart(this.elementRef.nativeElement, {
            data: this.data,
            options: this.options
        });
        Object.assign(this, this.chart);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: BoxplotChartComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: BoxplotChartComponent, selector: "ibm-boxplot-chart", usesInheritance: true, ngImport: i0, template: ``, isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: BoxplotChartComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'ibm-boxplot-chart',
                    template: ``
                }]
        }] });

/**
 * Wrapper around `BubbleChart` in carbon charts library
 *
 * Most functions just call their equivalent from the chart library.
 */
class BubbleChartComponent extends BaseChartComponent {
    /**
     * Runs after view init to create a chart, attach it to `elementRef` and draw it.
     */
    ngAfterViewInit() {
        this.chart = new BubbleChart(this.elementRef.nativeElement, {
            data: this.data,
            options: this.options
        });
        Object.assign(this, this.chart);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: BubbleChartComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: BubbleChartComponent, selector: "ibm-bubble-chart", usesInheritance: true, ngImport: i0, template: ``, isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: BubbleChartComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'ibm-bubble-chart',
                    template: ``
                }]
        }] });

/**
 * Wrapper around `BulletChart` in carbon charts library
 *
 * Most functions just call their equivalent from the chart library.
 */
class BulletChartComponent extends BaseChartComponent {
    /**
     * Runs after view init to create a chart, attach it to `elementRef` and draw it.
     */
    ngAfterViewInit() {
        this.chart = new BulletChart(this.elementRef.nativeElement, {
            data: this.data,
            options: this.options
        });
        Object.assign(this, this.chart);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: BulletChartComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: BulletChartComponent, selector: "ibm-bullet-chart", usesInheritance: true, ngImport: i0, template: ``, isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: BulletChartComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'ibm-bullet-chart',
                    template: ``
                }]
        }] });

/**
 * Wrapper around `CirclePackChart` in carbon charts library
 *
 * Most functions just call their equivalent from the chart library.
 */
class CirclePackChartComponent extends BaseChartComponent {
    /**
     * Runs after view init to create a chart, attach it to `elementRef` and draw it.
     */
    ngAfterViewInit() {
        this.chart = new CirclePackChart(this.elementRef.nativeElement, {
            data: this.data,
            options: this.options
        });
        Object.assign(this, this.chart);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: CirclePackChartComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: CirclePackChartComponent, selector: "ibm-circle-pack-chart", usesInheritance: true, ngImport: i0, template: ``, isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: CirclePackChartComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'ibm-circle-pack-chart',
                    template: ``
                }]
        }] });

/**
 * Wrapper around `ComboChart` in carbon charts library
 *
 * Most functions just call their equivalent from the chart library.
 */
class ComboChartComponent extends BaseChartComponent {
    /**
     * Runs after view init to create a chart, attach it to `elementRef` and draw it.
     */
    ngAfterViewInit() {
        this.chart = new ComboChart(this.elementRef.nativeElement, {
            data: this.data,
            options: this.options
        });
        Object.assign(this, this.chart);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: ComboChartComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: ComboChartComponent, selector: "ibm-combo-chart", usesInheritance: true, ngImport: i0, template: ``, isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: ComboChartComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'ibm-combo-chart',
                    template: ``
                }]
        }] });

/**
 * Wrapper around `DonutChart` in carbon charts library
 *
 * Most functions just call their equivalent from the chart library.
 */
class DonutChartComponent extends BaseChartComponent {
    /**
     * Runs after view init to create a chart, attach it to `elementRef` and draw it.
     */
    ngAfterViewInit() {
        this.chart = new DonutChart(this.elementRef.nativeElement, {
            data: this.data,
            options: this.options
        });
        Object.assign(this, this.chart);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: DonutChartComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: DonutChartComponent, selector: "ibm-donut-chart", usesInheritance: true, ngImport: i0, template: ``, isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: DonutChartComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'ibm-donut-chart',
                    template: ``
                }]
        }] });

/**
 * Wrapper around `Choropleth` in carbon charts library
 *
 * Most functions just call their equivalent from the chart library.
 */
class ExperimentalChoroplethChartComponent extends BaseChartComponent {
    /**
     * Runs after view init to create a chart, attach it to `elementRef` and draw it.
     */
    ngAfterViewInit() {
        this.chart = new ExperimentalChoroplethChart(this.elementRef.nativeElement, {
            data: this.data,
            options: this.options
        });
        Object.assign(this, this.chart);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: ExperimentalChoroplethChartComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: ExperimentalChoroplethChartComponent, selector: "ibm-experimental-choropleth-chart", usesInheritance: true, ngImport: i0, template: ``, isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: ExperimentalChoroplethChartComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'ibm-experimental-choropleth-chart',
                    template: ``
                }]
        }] });

/**
 * Wrapper around `GaugeChart` in carbon charts library
 *
 * Most functions just call their equivalent from the chart library.
 */
class GaugeChartComponent extends BaseChartComponent {
    /**
     * Runs after view init to create a chart, attach it to `elementRef` and draw it.
     */
    ngAfterViewInit() {
        this.chart = new GaugeChart(this.elementRef.nativeElement, {
            data: this.data,
            options: this.options
        });
        Object.assign(this, this.chart);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: GaugeChartComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: GaugeChartComponent, selector: "ibm-gauge-chart", usesInheritance: true, ngImport: i0, template: ``, isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: GaugeChartComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'ibm-gauge-chart',
                    template: ``
                }]
        }] });

/**
 * Wrapper around `GroupedBarChart` in carbon charts library
 *
 * Most functions just call their equivalent from the chart library.
 */
class GroupedBarChartComponent extends BaseChartComponent {
    /**
     * Runs after view init to create a chart, attach it to `elementRef` and draw it.
     */
    ngAfterViewInit() {
        this.chart = new GroupedBarChart(this.elementRef.nativeElement, {
            data: this.data,
            options: this.options
        });
        Object.assign(this, this.chart);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: GroupedBarChartComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: GroupedBarChartComponent, selector: "ibm-grouped-bar-chart", usesInheritance: true, ngImport: i0, template: ``, isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: GroupedBarChartComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'ibm-grouped-bar-chart',
                    template: ``
                }]
        }] });

/**
 * Wrapper around `Heatmap` in carbon charts library
 *
 * Most functions just call their equivalent from the chart library.
 */
class HeatmapChartComponent extends BaseChartComponent {
    /**
     * Runs after view init to create a chart, attach it to `elementRef` and draw it.
     */
    ngAfterViewInit() {
        this.chart = new HeatmapChart(this.elementRef.nativeElement, {
            data: this.data,
            options: this.options
        });
        Object.assign(this, this.chart);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: HeatmapChartComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: HeatmapChartComponent, selector: "ibm-heatmap-chart", usesInheritance: true, ngImport: i0, template: ``, isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: HeatmapChartComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'ibm-heatmap-chart',
                    template: ``
                }]
        }] });

/**
 * Wrapper around `HistogramChart` in carbon charts library
 *
 * Most functions just call their equivalent from the chart library.
 */
class HistogramChartComponent extends BaseChartComponent {
    /**
     * Runs after view init to create a chart, attach it to `elementRef` and draw it.
     */
    ngAfterViewInit() {
        this.chart = new HistogramChart(this.elementRef.nativeElement, {
            data: this.data,
            options: this.options
        });
        Object.assign(this, this.chart);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: HistogramChartComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: HistogramChartComponent, selector: "ibm-histogram-chart", usesInheritance: true, ngImport: i0, template: ``, isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: HistogramChartComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'ibm-histogram-chart',
                    template: ``
                }]
        }] });

/**
 * Wrapper around `LineChart` in carbon charts library
 *
 * Most functions just call their equivalent from the chart library.
 */
class LineChartComponent extends BaseChartComponent {
    /**
     * Runs after view init to create a chart, attach it to `elementRef` and draw it.
     */
    ngAfterViewInit() {
        this.chart = new LineChart(this.elementRef.nativeElement, {
            data: this.data,
            options: this.options
        });
        Object.assign(this, this.chart);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: LineChartComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: LineChartComponent, selector: "ibm-line-chart", usesInheritance: true, ngImport: i0, template: ``, isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: LineChartComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'ibm-line-chart',
                    template: ``
                }]
        }] });

/**
 * Wrapper around `LollipopChart` in carbon charts library
 *
 * Most functions just call their equivalent from the chart library.
 */
class LollipopChartComponent extends BaseChartComponent {
    /**
     * Runs after view init to create a chart, attach it to `elementRef` and draw it.
     */
    ngAfterViewInit() {
        this.chart = new LollipopChart(this.elementRef.nativeElement, {
            data: this.data,
            options: this.options
        });
        Object.assign(this, this.chart);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: LollipopChartComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: LollipopChartComponent, selector: "ibm-lollipop-chart", usesInheritance: true, ngImport: i0, template: ``, isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: LollipopChartComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'ibm-lollipop-chart',
                    template: ``
                }]
        }] });

/**
 * Wrapper around `MeterChart` in carbon charts library
 *
 * Most functions just call their equivalent from the chart library.
 */
class MeterChartComponent extends BaseChartComponent {
    /**
     * Runs after view init to create a chart, attach it to `elementRef` and draw it.
     */
    ngAfterViewInit() {
        this.chart = new MeterChart(this.elementRef.nativeElement, {
            data: this.data,
            options: this.options
        });
        Object.assign(this, this.chart);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: MeterChartComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: MeterChartComponent, selector: "ibm-meter-chart", usesInheritance: true, ngImport: i0, template: ``, isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: MeterChartComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'ibm-meter-chart',
                    template: ``
                }]
        }] });

/**
 * Wrapper around `PieChart` in carbon charts library
 *
 * Most functions just call their equivalent from the chart library.
 */
class PieChartComponent extends BaseChartComponent {
    /**
     * Runs after view init to create a chart, attach it to `elementRef` and draw it.
     */
    ngAfterViewInit() {
        this.chart = new PieChart(this.elementRef.nativeElement, {
            data: this.data,
            options: this.options
        });
        Object.assign(this, this.chart);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: PieChartComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: PieChartComponent, selector: "ibm-pie-chart", usesInheritance: true, ngImport: i0, template: ``, isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: PieChartComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'ibm-pie-chart',
                    template: ``
                }]
        }] });

/**
 * Wrapper around `RadarChart` in carbon charts library
 *
 * Most functions just call their equivalent from the chart library.
 */
class RadarChartComponent extends BaseChartComponent {
    /**
     * Runs after view init to create a chart, attach it to `elementRef` and draw it.
     */
    ngAfterViewInit() {
        this.chart = new RadarChart(this.elementRef.nativeElement, {
            data: this.data,
            options: this.options
        });
        Object.assign(this, this.chart);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: RadarChartComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: RadarChartComponent, selector: "ibm-radar-chart", usesInheritance: true, ngImport: i0, template: ``, isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: RadarChartComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'ibm-radar-chart',
                    template: ``
                }]
        }] });

/**
 * Wrapper around `ScatterChart` in carbon charts library
 *
 * Most functions just call their equivalent from the chart library.
 */
class ScatterChartComponent extends BaseChartComponent {
    /**
     * Runs after view init to create a chart, attach it to `elementRef` and draw it.
     */
    ngAfterViewInit() {
        this.chart = new ScatterChart(this.elementRef.nativeElement, {
            data: this.data,
            options: this.options
        });
        Object.assign(this, this.chart);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: ScatterChartComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: ScatterChartComponent, selector: "ibm-scatter-chart", usesInheritance: true, ngImport: i0, template: ``, isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: ScatterChartComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'ibm-scatter-chart',
                    template: ``
                }]
        }] });

/**
 * Wrapper around `SimpleBarChart` in carbon charts library
 *
 * Most functions just call their equivalent from the chart library.
 */
class SimpleBarChartComponent extends BaseChartComponent {
    /**
     * Runs after view init to create a chart, attach it to `elementRef` and draw it.
     */
    ngAfterViewInit() {
        this.chart = new SimpleBarChart(this.elementRef.nativeElement, {
            data: this.data,
            options: this.options
        });
        Object.assign(this, this.chart);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: SimpleBarChartComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: SimpleBarChartComponent, selector: "ibm-simple-bar-chart", usesInheritance: true, ngImport: i0, template: ``, isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: SimpleBarChartComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'ibm-simple-bar-chart',
                    template: ``
                }]
        }] });

/**
 * Wrapper around `StackedAreaChart` in carbon charts library
 *
 * Most functions just call their equivalent from the chart library.
 */
class StackedAreaChartComponent extends BaseChartComponent {
    /**
     * Runs after view init to create a chart, attach it to `elementRef` and draw it.
     */
    ngAfterViewInit() {
        this.chart = new StackedAreaChart(this.elementRef.nativeElement, {
            data: this.data,
            options: this.options
        });
        Object.assign(this, this.chart);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: StackedAreaChartComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: StackedAreaChartComponent, selector: "ibm-stacked-area-chart", usesInheritance: true, ngImport: i0, template: ``, isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: StackedAreaChartComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'ibm-stacked-area-chart',
                    template: ``
                }]
        }] });

/**
 * Wrapper around `StackedBarChart` in carbon charts library
 *
 * Most functions just call their equivalent from the chart library.
 */
class StackedBarChartComponent extends BaseChartComponent {
    /**
     * Runs after view init to create a chart, attach it to `elementRef` and draw it.
     */
    ngAfterViewInit() {
        this.chart = new StackedBarChart(this.elementRef.nativeElement, {
            data: this.data,
            options: this.options
        });
        Object.assign(this, this.chart);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: StackedBarChartComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: StackedBarChartComponent, selector: "ibm-stacked-bar-chart", usesInheritance: true, ngImport: i0, template: ``, isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: StackedBarChartComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'ibm-stacked-bar-chart',
                    template: ``
                }]
        }] });

/**
 * Wrapper around `TreeChart` in carbon charts library
 *
 * Most functions just call their equivalent from the chart library.
 */
class TreeChartComponent extends BaseChartComponent {
    /**
     * Runs after view init to create a chart, attach it to `elementRef` and draw it.
     */
    ngAfterViewInit() {
        this.chart = new TreeChart(this.elementRef.nativeElement, {
            data: this.data,
            options: this.options
        });
        Object.assign(this, this.chart);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: TreeChartComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: TreeChartComponent, selector: "ibm-tree-chart", usesInheritance: true, ngImport: i0, template: ``, isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: TreeChartComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'ibm-tree-chart',
                    template: ``
                }]
        }] });

/**
 * Wrapper around `TreemapChart` in carbon charts library
 *
 * Most functions just call their equivalent from the chart library.
 */
class TreemapChartComponent extends BaseChartComponent {
    /**
     * Runs after view init to create a chart, attach it to `elementRef` and draw it.
     */
    ngAfterViewInit() {
        this.chart = new TreemapChart(this.elementRef.nativeElement, {
            data: this.data,
            options: this.options
        });
        Object.assign(this, this.chart);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: TreemapChartComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: TreemapChartComponent, selector: "ibm-treemap-chart", usesInheritance: true, ngImport: i0, template: ``, isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: TreemapChartComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'ibm-treemap-chart',
                    template: ``
                }]
        }] });

/**
 * Wrapper around `WordCloudChart` in carbon charts library
 *
 * Most functions just call their equivalent from the chart library.
 */
class WordCloudChartComponent extends BaseChartComponent {
    /**
     * Runs after view init to create a chart, attach it to `elementRef` and draw it.
     */
    ngAfterViewInit() {
        this.chart = new WordCloudChart(this.elementRef.nativeElement, {
            data: this.data,
            options: this.options
        });
        Object.assign(this, this.chart);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: WordCloudChartComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: WordCloudChartComponent, selector: "ibm-wordcloud-chart", usesInheritance: true, ngImport: i0, template: ``, isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: WordCloudChartComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'ibm-wordcloud-chart',
                    template: ``
                }]
        }] });

class ChartsModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: ChartsModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.2.12", ngImport: i0, type: ChartsModule, declarations: [BaseChartComponent,
            AlluvialChartComponent,
            AreaChartComponent,
            StackedAreaChartComponent,
            SimpleBarChartComponent,
            GroupedBarChartComponent,
            StackedBarChartComponent,
            BoxplotChartComponent,
            BubbleChartComponent,
            BulletChartComponent,
            DonutChartComponent,
            ExperimentalChoroplethChartComponent,
            GaugeChartComponent,
            HeatmapChartComponent,
            HistogramChartComponent,
            LineChartComponent,
            LollipopChartComponent,
            PieChartComponent,
            ScatterChartComponent,
            MeterChartComponent,
            RadarChartComponent,
            ComboChartComponent,
            TreeChartComponent,
            TreemapChartComponent,
            CirclePackChartComponent,
            WordCloudChartComponent], imports: [CommonModule], exports: [BaseChartComponent,
            AlluvialChartComponent,
            AreaChartComponent,
            StackedAreaChartComponent,
            SimpleBarChartComponent,
            GroupedBarChartComponent,
            StackedBarChartComponent,
            BoxplotChartComponent,
            BubbleChartComponent,
            BulletChartComponent,
            DonutChartComponent,
            ExperimentalChoroplethChartComponent,
            GaugeChartComponent,
            HeatmapChartComponent,
            HistogramChartComponent,
            LineChartComponent,
            LollipopChartComponent,
            PieChartComponent,
            ScatterChartComponent,
            MeterChartComponent,
            RadarChartComponent,
            ComboChartComponent,
            TreeChartComponent,
            TreemapChartComponent,
            CirclePackChartComponent,
            WordCloudChartComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: ChartsModule, imports: [CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: ChartsModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        BaseChartComponent,
                        AlluvialChartComponent,
                        AreaChartComponent,
                        StackedAreaChartComponent,
                        SimpleBarChartComponent,
                        GroupedBarChartComponent,
                        StackedBarChartComponent,
                        BoxplotChartComponent,
                        BubbleChartComponent,
                        BulletChartComponent,
                        DonutChartComponent,
                        ExperimentalChoroplethChartComponent,
                        GaugeChartComponent,
                        HeatmapChartComponent,
                        HistogramChartComponent,
                        LineChartComponent,
                        LollipopChartComponent,
                        PieChartComponent,
                        ScatterChartComponent,
                        MeterChartComponent,
                        RadarChartComponent,
                        ComboChartComponent,
                        TreeChartComponent,
                        TreemapChartComponent,
                        CirclePackChartComponent,
                        WordCloudChartComponent
                    ],
                    imports: [CommonModule],
                    exports: [
                        BaseChartComponent,
                        AlluvialChartComponent,
                        AreaChartComponent,
                        StackedAreaChartComponent,
                        SimpleBarChartComponent,
                        GroupedBarChartComponent,
                        StackedBarChartComponent,
                        BoxplotChartComponent,
                        BubbleChartComponent,
                        BulletChartComponent,
                        DonutChartComponent,
                        ExperimentalChoroplethChartComponent,
                        GaugeChartComponent,
                        HeatmapChartComponent,
                        HistogramChartComponent,
                        LineChartComponent,
                        LollipopChartComponent,
                        PieChartComponent,
                        ScatterChartComponent,
                        MeterChartComponent,
                        RadarChartComponent,
                        ComboChartComponent,
                        TreeChartComponent,
                        TreemapChartComponent,
                        CirclePackChartComponent,
                        WordCloudChartComponent
                    ]
                }]
        }] });

const carbonPrefix = 'cds';

const template = `
<svg:marker
	[ngClass]="namespace"
	[attr.markerHeight]="height"
	[attr.markerWidth]="width"
	[attr.orient]="orient"
	[attr.id]="id"
	[attr.refX]="refX"
	[attr.refY]="refY"
	markerUnits="userSpaceOnUse">
	<svg:path [attr.d]="d" [ngStyle]="{'fill': color}" ></svg:path>
</svg:marker>
`;
class MarkerComponent {
    constructor() {
        this.d = '';
        this.color = '';
        this.id = '';
        this.orient = 'auto';
        this.height = '';
        this.width = '';
        this.refX = '';
        this.refY = '';
        this.position = 'end';
        this.namespace = `${carbonPrefix}--cc--marker`;
        this.setAttributes = ({ d, id, height, width }) => {
            const xPos = this.position === 'end' ? width / 2 + 0.5 : 0.5;
            const yPos = height / 2;
            this.d = this.d || d;
            this.id = this.id || id;
            this.height = this.height || height;
            this.width = this.width || width;
            this.refX = this.refX || xPos;
            this.refY = this.refY || yPos;
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: MarkerComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: MarkerComponent, selector: "ibm-graph-marker,[ibm-graph-marker]", inputs: { d: "d", color: "color", id: "id", orient: "orient", height: "height", width: "width", refX: "refX", refY: "refY", position: "position" }, ngImport: i0, template: "\n<svg:marker\n\t[ngClass]=\"namespace\"\n\t[attr.markerHeight]=\"height\"\n\t[attr.markerWidth]=\"width\"\n\t[attr.orient]=\"orient\"\n\t[attr.id]=\"id\"\n\t[attr.refX]=\"refX\"\n\t[attr.refY]=\"refY\"\n\tmarkerUnits=\"userSpaceOnUse\">\n\t<svg:path [attr.d]=\"d\" [ngStyle]=\"{'fill': color}\" ></svg:path>\n</svg:marker>\n", isInline: true, dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: MarkerComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'ibm-graph-marker,[ibm-graph-marker]',
                    template
                }]
        }], propDecorators: { d: [{
                type: Input
            }], color: [{
                type: Input
            }], id: [{
                type: Input
            }], orient: [{
                type: Input
            }], height: [{
                type: Input
            }], width: [{
                type: Input
            }], refX: [{
                type: Input
            }], refY: [{
                type: Input
            }], position: [{
                type: Input
            }] } });
class MarkerArrowLeftComponent extends MarkerComponent {
    ngOnInit() {
        this.setAttributes({ ...arrowLeft });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: MarkerArrowLeftComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: MarkerArrowLeftComponent, selector: "ibm-graph-marker-arrow-left,[ibm-graph-marker-arrow-left]", usesInheritance: true, ngImport: i0, template: "\n<svg:marker\n\t[ngClass]=\"namespace\"\n\t[attr.markerHeight]=\"height\"\n\t[attr.markerWidth]=\"width\"\n\t[attr.orient]=\"orient\"\n\t[attr.id]=\"id\"\n\t[attr.refX]=\"refX\"\n\t[attr.refY]=\"refY\"\n\tmarkerUnits=\"userSpaceOnUse\">\n\t<svg:path [attr.d]=\"d\" [ngStyle]=\"{'fill': color}\" ></svg:path>\n</svg:marker>\n", isInline: true, dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: MarkerArrowLeftComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'ibm-graph-marker-arrow-left,[ibm-graph-marker-arrow-left]',
                    template
                }]
        }] });
class MarkerArrowRightComponent extends MarkerComponent {
    ngOnInit() {
        this.setAttributes({ ...arrowRight });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: MarkerArrowRightComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: MarkerArrowRightComponent, selector: "ibm-graph-marker-arrow-right,[ibm-graph-marker-arrow-right]", usesInheritance: true, ngImport: i0, template: "\n<svg:marker\n\t[ngClass]=\"namespace\"\n\t[attr.markerHeight]=\"height\"\n\t[attr.markerWidth]=\"width\"\n\t[attr.orient]=\"orient\"\n\t[attr.id]=\"id\"\n\t[attr.refX]=\"refX\"\n\t[attr.refY]=\"refY\"\n\tmarkerUnits=\"userSpaceOnUse\">\n\t<svg:path [attr.d]=\"d\" [ngStyle]=\"{'fill': color}\" ></svg:path>\n</svg:marker>\n", isInline: true, dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: MarkerArrowRightComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'ibm-graph-marker-arrow-right,[ibm-graph-marker-arrow-right]',
                    template
                }]
        }] });
class MarkerShapeNodeComponent extends MarkerComponent {
    ngOnInit() {
        this.setAttributes({ ...circle });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: MarkerShapeNodeComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: MarkerShapeNodeComponent, selector: "ibm-graph-marker-circle,[ibm-graph-marker-circle]", usesInheritance: true, ngImport: i0, template: "\n<svg:marker\n\t[ngClass]=\"namespace\"\n\t[attr.markerHeight]=\"height\"\n\t[attr.markerWidth]=\"width\"\n\t[attr.orient]=\"orient\"\n\t[attr.id]=\"id\"\n\t[attr.refX]=\"refX\"\n\t[attr.refY]=\"refY\"\n\tmarkerUnits=\"userSpaceOnUse\">\n\t<svg:path [attr.d]=\"d\" [ngStyle]=\"{'fill': color}\" ></svg:path>\n</svg:marker>\n", isInline: true, dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: MarkerShapeNodeComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'ibm-graph-marker-circle,[ibm-graph-marker-circle]',
                    template
                }]
        }] });
class MarkerDiamondComponent extends MarkerComponent {
    ngOnInit() {
        this.setAttributes({ ...diamond });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: MarkerDiamondComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: MarkerDiamondComponent, selector: "ibm-graph-marker-diamond,[ibm-graph-marker-diamond]", usesInheritance: true, ngImport: i0, template: "\n<svg:marker\n\t[ngClass]=\"namespace\"\n\t[attr.markerHeight]=\"height\"\n\t[attr.markerWidth]=\"width\"\n\t[attr.orient]=\"orient\"\n\t[attr.id]=\"id\"\n\t[attr.refX]=\"refX\"\n\t[attr.refY]=\"refY\"\n\tmarkerUnits=\"userSpaceOnUse\">\n\t<svg:path [attr.d]=\"d\" [ngStyle]=\"{'fill': color}\" ></svg:path>\n</svg:marker>\n", isInline: true, dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: MarkerDiamondComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'ibm-graph-marker-diamond,[ibm-graph-marker-diamond]',
                    template
                }]
        }] });
class MarkerSquareComponent extends MarkerComponent {
    ngOnInit() {
        this.setAttributes({ ...square });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: MarkerSquareComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: MarkerSquareComponent, selector: "ibm-graph-marker-square,[ibm-graph-marker-square]", usesInheritance: true, ngImport: i0, template: "\n<svg:marker\n\t[ngClass]=\"namespace\"\n\t[attr.markerHeight]=\"height\"\n\t[attr.markerWidth]=\"width\"\n\t[attr.orient]=\"orient\"\n\t[attr.id]=\"id\"\n\t[attr.refX]=\"refX\"\n\t[attr.refY]=\"refY\"\n\tmarkerUnits=\"userSpaceOnUse\">\n\t<svg:path [attr.d]=\"d\" [ngStyle]=\"{'fill': color}\" ></svg:path>\n</svg:marker>\n", isInline: true, dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: MarkerSquareComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'ibm-graph-marker-square,[ibm-graph-marker-square]',
                    template
                }]
        }] });
class MarkerTeeComponent extends MarkerComponent {
    ngOnInit() {
        this.setAttributes({ ...tee });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: MarkerTeeComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: MarkerTeeComponent, selector: "ibm-graph-marker-tee,[ibm-graph-marker-tee]", usesInheritance: true, ngImport: i0, template: "\n<svg:marker\n\t[ngClass]=\"namespace\"\n\t[attr.markerHeight]=\"height\"\n\t[attr.markerWidth]=\"width\"\n\t[attr.orient]=\"orient\"\n\t[attr.id]=\"id\"\n\t[attr.refX]=\"refX\"\n\t[attr.refY]=\"refY\"\n\tmarkerUnits=\"userSpaceOnUse\">\n\t<svg:path [attr.d]=\"d\" [ngStyle]=\"{'fill': color}\" ></svg:path>\n</svg:marker>\n", isInline: true, dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: MarkerTeeComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'ibm-graph-marker-tee,[ibm-graph-marker-tee]',
                    template
                }]
        }] });

class MarkerModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: MarkerModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.2.12", ngImport: i0, type: MarkerModule, declarations: [MarkerComponent,
            MarkerArrowLeftComponent,
            MarkerArrowRightComponent,
            MarkerShapeNodeComponent,
            MarkerDiamondComponent,
            MarkerSquareComponent,
            MarkerTeeComponent], imports: [CommonModule], exports: [MarkerComponent,
            MarkerArrowLeftComponent,
            MarkerArrowRightComponent,
            MarkerShapeNodeComponent,
            MarkerDiamondComponent,
            MarkerSquareComponent,
            MarkerTeeComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: MarkerModule, imports: [CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: MarkerModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        MarkerComponent,
                        MarkerArrowLeftComponent,
                        MarkerArrowRightComponent,
                        MarkerShapeNodeComponent,
                        MarkerDiamondComponent,
                        MarkerSquareComponent,
                        MarkerTeeComponent
                    ],
                    imports: [CommonModule],
                    exports: [
                        MarkerComponent,
                        MarkerArrowLeftComponent,
                        MarkerArrowRightComponent,
                        MarkerShapeNodeComponent,
                        MarkerDiamondComponent,
                        MarkerSquareComponent,
                        MarkerTeeComponent
                    ]
                }]
        }] });

class EdgeComponent {
    constructor() {
        this.color = '';
        this.markerEnd = '';
        this.markerStart = '';
        this.source = { x: 0, y: 0 };
        this.target = { x: 0, y: 0 };
        this.path = '';
        this.pathClasses = '';
        this.namespace = `${carbonPrefix}--cc--edge`;
        this.straight = buildStraightPathString;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: EdgeComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: EdgeComponent, selector: "ibm-graph-edge, [ibm-graph-edge]", inputs: { color: "color", markerEnd: "markerEnd", markerStart: "markerStart", source: "source", target: "target", variant: "variant", path: "path" }, ngImport: i0, template: `
		<svg:g [ngClass]="[namespace, variant ? namespace + '--' + variant : '']">
			<svg:path
				[ngClass]="namespace + '__container'"
				[attr.d]="path ? path : straight(source, target)" />
			<svg:path
				[ngClass]="namespace + '__outer'"
				[attr.d]="path ? path : straight(source, target)" />
			<svg:path
				[ngClass]="namespace + '__inner'"
				[attr.d]="path ? path : straight(source, target)"
				[ngStyle]="{ stroke: color }"
				[attr.marker-start]="markerStart ? 'url(#' + markerStart + ')' : ''"
				[attr.marker-end]="markerEnd ? 'url(#' + markerEnd + ')' : ''" />
		</svg:g>
	`, isInline: true, dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: EdgeComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'ibm-graph-edge, [ibm-graph-edge]',
                    template: `
		<svg:g [ngClass]="[namespace, variant ? namespace + '--' + variant : '']">
			<svg:path
				[ngClass]="namespace + '__container'"
				[attr.d]="path ? path : straight(source, target)" />
			<svg:path
				[ngClass]="namespace + '__outer'"
				[attr.d]="path ? path : straight(source, target)" />
			<svg:path
				[ngClass]="namespace + '__inner'"
				[attr.d]="path ? path : straight(source, target)"
				[ngStyle]="{ stroke: color }"
				[attr.marker-start]="markerStart ? 'url(#' + markerStart + ')' : ''"
				[attr.marker-end]="markerEnd ? 'url(#' + markerEnd + ')' : ''" />
		</svg:g>
	`
                }]
        }], propDecorators: { color: [{
                type: Input
            }], markerEnd: [{
                type: Input
            }], markerStart: [{
                type: Input
            }], source: [{
                type: Input
            }], target: [{
                type: Input
            }], variant: [{
                type: Input
            }], path: [{
                type: Input
            }] } });

class EdgeModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: EdgeModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.2.12", ngImport: i0, type: EdgeModule, declarations: [EdgeComponent], imports: [CommonModule], exports: [EdgeComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: EdgeModule, imports: [CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: EdgeModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [EdgeComponent],
                    imports: [CommonModule],
                    exports: [EdgeComponent]
                }]
        }] });

class CardNodeComponent {
    constructor() {
        this.as = 'div';
        this.href = '';
        this.color = '';
        this.stacked = false;
        this.position = 'static';
        // eslint-disable-next-line @angular-eslint/no-output-native
        this.click = new EventEmitter();
        this.mouseEnter = new EventEmitter();
        this.mouseOver = new EventEmitter();
        this.mouseOut = new EventEmitter();
        this.mouseLeave = new EventEmitter();
        this.mouseMove = new EventEmitter();
        this.namespace = `${carbonPrefix}--cc--card-node`;
        this.component = 'div';
    }
    ngOnInit() {
        if (this.href) {
            this.component = 'a';
        }
        else {
            this.component = this.as;
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: CardNodeComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: CardNodeComponent, selector: "ibm-diagram-card-node", inputs: { as: "as", href: "href", color: "color", stacked: "stacked", position: "position" }, outputs: { click: "click", mouseEnter: "mouseEnter", mouseOver: "mouseOver", mouseOut: "mouseOut", mouseLeave: "mouseLeave", mouseMove: "mouseMove" }, ngImport: i0, template: `
		<ng-container [ngSwitch]="component">
			<xhtml:div
				*ngSwitchCase="'div'"
				[ngClass]="[
					namespace,
					stacked ? namespace + '--stacked' : '',
					namespace + '--' + component
				]"
				[ngStyle]="{
					'border-color': color,
					position: position
				}"
				(mouseenter)="mouseEnter.emit($event)"
				(mouseover)="mouseOver.emit($event)"
				(mouseout)="mouseOut.emit($event)"
				(mouseleave)="mouseLeave.emit($event)"
				(mousemove)="mouseMove.emit($event)"
				tabindex="0">
				<ng-container *ngTemplateOutlet="nodeTemplate"></ng-container>
			</xhtml:div>

			<xhtml:button
				*ngSwitchCase="'button'"
				[ngClass]="[
					namespace,
					stacked ? namespace + '--stacked' : '',
					namespace + '--' + component
				]"
				[ngStyle]="{
					'border-color': color,
					position: position
				}"
				(click)="click.emit($event)"
				(mouseenter)="mouseEnter.emit($event)"
				(mouseover)="mouseOver.emit($event)"
				(mouseout)="mouseOut.emit($event)"
				(mouseleave)="mouseLeave.emit($event)"
				(mousemove)="mouseMove.emit($event)"
				tabindex="0">
				<ng-container *ngTemplateOutlet="nodeTemplate"></ng-container>
			</xhtml:button>

			<xhtml:a
				*ngSwitchCase="'a'"
				[ngClass]="[
					namespace,
					stacked ? namespace + '--stacked' : '',
					namespace + '--' + component
				]"
				[attr.href]="href"
				[ngStyle]="{ 'border-color': color, position: position }"
				(mouseenter)="mouseEnter.emit($event)"
				(mouseover)="mouseOver.emit($event)"
				(mouseout)="mouseOut.emit($event)"
				(mouseleave)="mouseLeave.emit($event)"
				(mousemove)="mouseMove.emit($event)"
				tabindex="0">
				<ng-container *ngTemplateOutlet="nodeTemplate"></ng-container>
			</xhtml:a>
		</ng-container>
		<ng-template #nodeTemplate>
			<ng-content></ng-content>
		</ng-template>
	`, isInline: true, dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "directive", type: i1.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }, { kind: "directive", type: i1.NgSwitch, selector: "[ngSwitch]", inputs: ["ngSwitch"] }, { kind: "directive", type: i1.NgSwitchCase, selector: "[ngSwitchCase]", inputs: ["ngSwitchCase"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: CardNodeComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'ibm-diagram-card-node',
                    template: `
		<ng-container [ngSwitch]="component">
			<xhtml:div
				*ngSwitchCase="'div'"
				[ngClass]="[
					namespace,
					stacked ? namespace + '--stacked' : '',
					namespace + '--' + component
				]"
				[ngStyle]="{
					'border-color': color,
					position: position
				}"
				(mouseenter)="mouseEnter.emit($event)"
				(mouseover)="mouseOver.emit($event)"
				(mouseout)="mouseOut.emit($event)"
				(mouseleave)="mouseLeave.emit($event)"
				(mousemove)="mouseMove.emit($event)"
				tabindex="0">
				<ng-container *ngTemplateOutlet="nodeTemplate"></ng-container>
			</xhtml:div>

			<xhtml:button
				*ngSwitchCase="'button'"
				[ngClass]="[
					namespace,
					stacked ? namespace + '--stacked' : '',
					namespace + '--' + component
				]"
				[ngStyle]="{
					'border-color': color,
					position: position
				}"
				(click)="click.emit($event)"
				(mouseenter)="mouseEnter.emit($event)"
				(mouseover)="mouseOver.emit($event)"
				(mouseout)="mouseOut.emit($event)"
				(mouseleave)="mouseLeave.emit($event)"
				(mousemove)="mouseMove.emit($event)"
				tabindex="0">
				<ng-container *ngTemplateOutlet="nodeTemplate"></ng-container>
			</xhtml:button>

			<xhtml:a
				*ngSwitchCase="'a'"
				[ngClass]="[
					namespace,
					stacked ? namespace + '--stacked' : '',
					namespace + '--' + component
				]"
				[attr.href]="href"
				[ngStyle]="{ 'border-color': color, position: position }"
				(mouseenter)="mouseEnter.emit($event)"
				(mouseover)="mouseOver.emit($event)"
				(mouseout)="mouseOut.emit($event)"
				(mouseleave)="mouseLeave.emit($event)"
				(mousemove)="mouseMove.emit($event)"
				tabindex="0">
				<ng-container *ngTemplateOutlet="nodeTemplate"></ng-container>
			</xhtml:a>
		</ng-container>
		<ng-template #nodeTemplate>
			<ng-content></ng-content>
		</ng-template>
	`
                }]
        }], propDecorators: { as: [{
                type: Input
            }], href: [{
                type: Input
            }], color: [{
                type: Input
            }], stacked: [{
                type: Input
            }], position: [{
                type: Input
            }], click: [{
                type: Output
            }], mouseEnter: [{
                type: Output
            }], mouseOver: [{
                type: Output
            }], mouseOut: [{
                type: Output
            }], mouseLeave: [{
                type: Output
            }], mouseMove: [{
                type: Output
            }] } });

class CardNodeColumnComponent {
    constructor() {
        this.farsideColumn = false;
    }
    get class() {
        const farsideClassName = this.farsideColumn
            ? `${carbonPrefix}--cc--card-node__column--farside`
            : '';
        return `${carbonPrefix}--cc--card-node__column ${farsideClassName}`;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: CardNodeColumnComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: CardNodeColumnComponent, selector: "ibm-diagram-card-node-column", inputs: { farsideColumn: "farsideColumn" }, host: { properties: { "class": "this.class" } }, ngImport: i0, template: `
		<xhtml:div>
			<ng-content></ng-content>
		</xhtml:div>
	`, isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: CardNodeColumnComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'ibm-diagram-card-node-column',
                    template: `
		<xhtml:div>
			<ng-content></ng-content>
		</xhtml:div>
	`
                }]
        }], propDecorators: { farsideColumn: [{
                type: Input
            }], class: [{
                type: HostBinding,
                args: ['class']
            }] } });

class CardNodeLabelComponent {
    constructor() {
        this.namespace = `${carbonPrefix}--cc--card-node__label`;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: CardNodeLabelComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: CardNodeLabelComponent, selector: "ibm-diagram-card-node-label", ngImport: i0, template: `
		<xhtml:label [ngClass]="namespace">
			<ng-content></ng-content>
		</xhtml:label>
	`, isInline: true, dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: CardNodeLabelComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'ibm-diagram-card-node-label',
                    template: `
		<xhtml:label [ngClass]="namespace">
			<ng-content></ng-content>
		</xhtml:label>
	`
                }]
        }] });

class CardNodeSubtitleComponent {
    constructor() {
        this.namespace = `${carbonPrefix}--cc--card-node__subtitle`;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: CardNodeSubtitleComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: CardNodeSubtitleComponent, selector: "ibm-diagram-card-node-subtitle", ngImport: i0, template: `
		<xhtml:div [ngClass]="namespace">
			<ng-content></ng-content>
		</xhtml:div>
	`, isInline: true, dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: CardNodeSubtitleComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'ibm-diagram-card-node-subtitle',
                    template: `
		<xhtml:div [ngClass]="namespace">
			<ng-content></ng-content>
		</xhtml:div>
	`
                }]
        }] });

class CardNodeTitleComponent {
    constructor() {
        this.namespace = `${carbonPrefix}--cc--card-node__title`;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: CardNodeTitleComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: CardNodeTitleComponent, selector: "ibm-diagram-card-node-title", ngImport: i0, template: `
		<xhtml:div [ngClass]="namespace">
			<ng-content></ng-content>
		</xhtml:div>
	`, isInline: true, dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: CardNodeTitleComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'ibm-diagram-card-node-title',
                    template: `
		<xhtml:div [ngClass]="namespace">
			<ng-content></ng-content>
		</xhtml:div>
	`
                }]
        }] });

class CardNodeModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: CardNodeModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.2.12", ngImport: i0, type: CardNodeModule, declarations: [CardNodeComponent,
            CardNodeColumnComponent,
            CardNodeLabelComponent,
            CardNodeSubtitleComponent,
            CardNodeTitleComponent], imports: [CommonModule], exports: [CardNodeComponent,
            CardNodeColumnComponent,
            CardNodeLabelComponent,
            CardNodeSubtitleComponent,
            CardNodeTitleComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: CardNodeModule, imports: [CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: CardNodeModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        CardNodeComponent,
                        CardNodeColumnComponent,
                        CardNodeLabelComponent,
                        CardNodeSubtitleComponent,
                        CardNodeTitleComponent
                    ],
                    imports: [CommonModule],
                    exports: [
                        CardNodeComponent,
                        CardNodeColumnComponent,
                        CardNodeLabelComponent,
                        CardNodeSubtitleComponent,
                        CardNodeTitleComponent
                    ]
                }]
        }] });

class ShapeNodeComponent {
    constructor() {
        this.as = 'div';
        this.href = '';
        this.size = 48;
        this.stacked = false;
        this.shape = 'circle';
        this.subtitle = '';
        this.title = '';
        this.position = 'fixed';
        this.bodyPosition = 'absolute';
        // eslint-disable-next-line @angular-eslint/no-output-native
        this.click = new EventEmitter();
        this.mouseEnter = new EventEmitter();
        this.mouseOver = new EventEmitter();
        this.mouseOut = new EventEmitter();
        this.mouseLeave = new EventEmitter();
        this.mouseMove = new EventEmitter();
        this.namespace = `${carbonPrefix}--cc--shape-node`;
        this.component = 'div';
    }
    ngOnInit() {
        if (this.href) {
            this.component = 'a';
        }
        else {
            this.component = this.as;
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: ShapeNodeComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: ShapeNodeComponent, selector: "ibm-diagram-shape-node", inputs: { as: "as", href: "href", renderIcon: "renderIcon", size: "size", stacked: "stacked", shape: "shape", subtitle: "subtitle", title: "title", position: "position", bodyPosition: "bodyPosition" }, outputs: { click: "click", mouseEnter: "mouseEnter", mouseOver: "mouseOver", mouseOut: "mouseOut", mouseLeave: "mouseLeave", mouseMove: "mouseMove" }, ngImport: i0, template: `
		<ng-container [ngSwitch]="component">
			<xhtml:div
				*ngSwitchCase="'div'"
				[ngClass]="[
					namespace,
					stacked ? namespace + '--stacked' : '',
					shape ? namespace + '--' + shape : '',
					namespace + '--' + component
				]"
				[ngStyle]="{
					'height.px': size,
					'width.px': size,
					position: position
				}"
				(mouseenter)="mouseEnter.emit($event)"
				(mouseover)="mouseOver.emit($event)"
				(mouseout)="mouseOut.emit($event)"
				(mouseleave)="mouseLeave.emit($event)"
				(mousemove)="mouseMove.emit($event)"
				tabindex="0">
				<ng-container *ngTemplateOutlet="nodeTemplate"></ng-container>
			</xhtml:div>

			<xhtml:button
				*ngSwitchCase="'button'"
				[ngClass]="[
					namespace,
					stacked ? namespace + '--stacked' : '',
					shape ? namespace + '--' + shape : '',
					namespace + '--' + component
				]"
				[ngStyle]="{
					'height.px': size,
					'width.px': size,
					position: position
				}"
				(click)="click.emit($event)"
				(mouseenter)="mouseEnter.emit($event)"
				(mouseover)="mouseOver.emit($event)"
				(mouseout)="mouseOut.emit($event)"
				(mouseleave)="mouseLeave.emit($event)"
				(mousemove)="mouseMove.emit($event)"
				tabindex="0">
				<ng-container *ngTemplateOutlet="nodeTemplate"></ng-container>
			</xhtml:button>

			<xhtml:a
				*ngSwitchCase="'a'"
				[ngClass]="[
					namespace,
					stacked ? namespace + '--stacked' : '',
					shape ? namespace + '--' + shape : '',
					namespace + '--' + component
				]"
				[attr.href]="href"
				[ngStyle]="{
					'height.px': size,
					'width.px': size,
					position: position
				}"
				(mouseenter)="mouseEnter.emit($event)"
				(mouseover)="mouseOver.emit($event)"
				(mouseout)="mouseOut.emit($event)"
				(mouseleave)="mouseLeave.emit($event)"
				(mousemove)="mouseMove.emit($event)"
				tabindex="0">
				<ng-container *ngTemplateOutlet="nodeTemplate"></ng-container>
			</xhtml:a>
		</ng-container>

		<ng-template #nodeTemplate>
			<div *ngIf="renderIcon" attr.class="{{ namespace + '__icon' }}">
				<ng-container *ngTemplateOutlet="renderIcon"></ng-container>
			</div>
			<div attr.class="{{ namespace + '__body' }}" [ngStyle]="{ position: bodyPosition }">
				<div attr.class="{{ namespace + '__title' }}">{{ title }}</div>
				<div attr.class="{{ namespace + '__subtitle' }}">
					{{ subtitle }}
				</div>
			</div>
		</ng-template>
	`, isInline: true, dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "directive", type: i1.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }, { kind: "directive", type: i1.NgSwitch, selector: "[ngSwitch]", inputs: ["ngSwitch"] }, { kind: "directive", type: i1.NgSwitchCase, selector: "[ngSwitchCase]", inputs: ["ngSwitchCase"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: ShapeNodeComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'ibm-diagram-shape-node',
                    template: `
		<ng-container [ngSwitch]="component">
			<xhtml:div
				*ngSwitchCase="'div'"
				[ngClass]="[
					namespace,
					stacked ? namespace + '--stacked' : '',
					shape ? namespace + '--' + shape : '',
					namespace + '--' + component
				]"
				[ngStyle]="{
					'height.px': size,
					'width.px': size,
					position: position
				}"
				(mouseenter)="mouseEnter.emit($event)"
				(mouseover)="mouseOver.emit($event)"
				(mouseout)="mouseOut.emit($event)"
				(mouseleave)="mouseLeave.emit($event)"
				(mousemove)="mouseMove.emit($event)"
				tabindex="0">
				<ng-container *ngTemplateOutlet="nodeTemplate"></ng-container>
			</xhtml:div>

			<xhtml:button
				*ngSwitchCase="'button'"
				[ngClass]="[
					namespace,
					stacked ? namespace + '--stacked' : '',
					shape ? namespace + '--' + shape : '',
					namespace + '--' + component
				]"
				[ngStyle]="{
					'height.px': size,
					'width.px': size,
					position: position
				}"
				(click)="click.emit($event)"
				(mouseenter)="mouseEnter.emit($event)"
				(mouseover)="mouseOver.emit($event)"
				(mouseout)="mouseOut.emit($event)"
				(mouseleave)="mouseLeave.emit($event)"
				(mousemove)="mouseMove.emit($event)"
				tabindex="0">
				<ng-container *ngTemplateOutlet="nodeTemplate"></ng-container>
			</xhtml:button>

			<xhtml:a
				*ngSwitchCase="'a'"
				[ngClass]="[
					namespace,
					stacked ? namespace + '--stacked' : '',
					shape ? namespace + '--' + shape : '',
					namespace + '--' + component
				]"
				[attr.href]="href"
				[ngStyle]="{
					'height.px': size,
					'width.px': size,
					position: position
				}"
				(mouseenter)="mouseEnter.emit($event)"
				(mouseover)="mouseOver.emit($event)"
				(mouseout)="mouseOut.emit($event)"
				(mouseleave)="mouseLeave.emit($event)"
				(mousemove)="mouseMove.emit($event)"
				tabindex="0">
				<ng-container *ngTemplateOutlet="nodeTemplate"></ng-container>
			</xhtml:a>
		</ng-container>

		<ng-template #nodeTemplate>
			<div *ngIf="renderIcon" attr.class="{{ namespace + '__icon' }}">
				<ng-container *ngTemplateOutlet="renderIcon"></ng-container>
			</div>
			<div attr.class="{{ namespace + '__body' }}" [ngStyle]="{ position: bodyPosition }">
				<div attr.class="{{ namespace + '__title' }}">{{ title }}</div>
				<div attr.class="{{ namespace + '__subtitle' }}">
					{{ subtitle }}
				</div>
			</div>
		</ng-template>
	`
                }]
        }], propDecorators: { as: [{
                type: Input
            }], href: [{
                type: Input
            }], renderIcon: [{
                type: Input
            }], size: [{
                type: Input
            }], stacked: [{
                type: Input
            }], shape: [{
                type: Input
            }], subtitle: [{
                type: Input
            }], title: [{
                type: Input
            }], position: [{
                type: Input
            }], bodyPosition: [{
                type: Input
            }], click: [{
                type: Output
            }], mouseEnter: [{
                type: Output
            }], mouseOver: [{
                type: Output
            }], mouseOut: [{
                type: Output
            }], mouseLeave: [{
                type: Output
            }], mouseMove: [{
                type: Output
            }] } });

class ShapeNodeModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: ShapeNodeModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.2.12", ngImport: i0, type: ShapeNodeModule, declarations: [ShapeNodeComponent], imports: [CommonModule], exports: [ShapeNodeComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: ShapeNodeModule, imports: [CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: ShapeNodeModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [ShapeNodeComponent],
                    imports: [CommonModule],
                    exports: [ShapeNodeComponent]
                }]
        }] });

/*
 * Public API Surface of charts-angular
 */

/**
 * Generated bundle index. Do not edit.
 */

export { AlluvialChartComponent, AreaChartComponent, BaseChartComponent, BoxplotChartComponent, BubbleChartComponent, BulletChartComponent, CardNodeColumnComponent, CardNodeComponent, CardNodeLabelComponent, CardNodeModule, CardNodeSubtitleComponent, CardNodeTitleComponent, ChartsModule, CirclePackChartComponent, ComboChartComponent, DonutChartComponent, EdgeComponent, EdgeModule, ExperimentalChoroplethChartComponent, GaugeChartComponent, GroupedBarChartComponent, HeatmapChartComponent, HistogramChartComponent, LineChartComponent, LollipopChartComponent, MarkerArrowLeftComponent, MarkerArrowRightComponent, MarkerComponent, MarkerDiamondComponent, MarkerModule, MarkerShapeNodeComponent, MarkerSquareComponent, MarkerTeeComponent, MeterChartComponent, PieChartComponent, RadarChartComponent, ScatterChartComponent, ShapeNodeComponent, ShapeNodeModule, SimpleBarChartComponent, StackedAreaChartComponent, StackedBarChartComponent, TreeChartComponent, TreemapChartComponent, WordCloudChartComponent };
